import { FormEvent, useMemo, useRef, useState } from 'react';
import { KeyedMutator } from 'swr';
import { useClient } from '../../../hooks/useClient';
import { useForm } from '../../../hooks/useForm';
import { useLocaleContext } from '../../../context/LocaleContext';
import { Branch } from '../../../types/branch';
import { Organization } from '../../../types/organization';
import { BranchSettingsForm, getBranchSettingsFormValidators, getInitialBranchSettingsForm } from '../validators';
import { CityOption, searchCity } from '../utils/searchCity';

interface Options {
  selectedBranch: Branch | undefined;
  selectedBranchId: string | undefined;
  orgHasAnySignedDocumentsFeature: boolean;
  mutateOrganization: KeyedMutator<Organization | undefined>;
}

export const useBranchSettingsForm = ({
  selectedBranch,
  selectedBranchId,
  orgHasAnySignedDocumentsFeature,
  mutateOrganization,
}: Options) => {
  const { patch } = useClient();
  const { locale } = useLocaleContext();
  const [cities, setCities] = useState<CityOption[]>([]);

  const initialBranchSettingsForm = useMemo(() => getInitialBranchSettingsForm(selectedBranch), [selectedBranch]);
  const branchSettingsFormValidators = useMemo(
    () => getBranchSettingsFormValidators(orgHasAnySignedDocumentsFeature),
    [orgHasAnySignedDocumentsFeature],
  );
  const branchSettingsFormRef = useRef(null);
  const {
    form: branchSettings,
    setFormFields: setBranchSettingsField,
    isSaving: branchSettingsSaving,
    setIsSaving: setBranchSettingsSaving,
    hasChanged: branchSettingsChanged,
    validate: validateBranchSettings,
    validationErrors: branchSettingsErrors,
  } = useForm<BranchSettingsForm | undefined>(
    initialBranchSettingsForm,
    false,
    branchSettingsFormValidators,
    branchSettingsFormRef,
  );
  const handleSaveBranchSettings = async (e: FormEvent) => {
    e.preventDefault();
    const errors = validateBranchSettings();
    if (errors || !branchSettings || !selectedBranchId) return;
    setBranchSettingsSaving(true);

    const updatedBranch = await patch<Branch>(`/branches/${selectedBranchId}`, {
      body: { ...branchSettings },
      errorMessages: {
        summary: 'Error al cambiar datos de la sucursal',
        defaultDetail: 'No se ha podido modificar los datos de la sucursal',
      },
      successMessage: {
        summary: 'Datos de la sucursal modificados',
        detail: 'Los datos de la sucursal se han modificado correctamente',
      },
    });

    if (updatedBranch) {
      mutateOrganization?.();
    }
    setBranchSettingsSaving(false);
  };

  const handleCitySearch = async (event: { query: string }) => {
    const results = await searchCity(event.query, locale);
    setCities(results);
  };

  return {
    formRef: branchSettingsFormRef,
    form: branchSettings,
    setFormFields: setBranchSettingsField,
    isSaving: branchSettingsSaving,
    hasChanged: branchSettingsChanged,
    validationErrors: branchSettingsErrors,
    handleSave: handleSaveBranchSettings,
    cities,
    handleCitySearch,
  };
};

export type BranchSettingsFormState = ReturnType<typeof useBranchSettingsForm>;
